import { useEffect, useState } from 'react';
import { Button, Card, DatePicker, Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import AppTypography from './typography/Typography';
import { getDynamicCommonCount } from '../services/dashboardApi';
import {
  getCandidateSummaryItems,
  getJobSummaryItemsFromApi,
  getSubmissionSummaryFromApi,
  getSummaryRequest,
} from './viewSummaryConfig';

const { RangePicker } = DatePicker;

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last-7-days', label: 'Last 7 Days' },
  { value: 'last-30-days', label: 'Last 30 Days' },
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'custom-range', label: 'Custom Range' },
];

function getSummaryPopupContainer(triggerNode) {
  return triggerNode.closest('.view-summary-card') ?? document.body;
}

function formatDateParam(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function addDays(date, days) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);

  return nextDate;
}

function getDateRangeForPreset(rangeKey) {
  const today = new Date();
  const startOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

  if (rangeKey === 'today') return { startDate: today, endDate: today };
  if (rangeKey === 'yesterday') {
    const yesterday = addDays(today, -1);
    return { startDate: yesterday, endDate: yesterday };
  }
  if (rangeKey === 'last-30-days') return { startDate: addDays(today, -29), endDate: today };
  if (rangeKey === 'this-month') return { startDate: startOfThisMonth, endDate: today };
  if (rangeKey === 'last-month') return { startDate: startOfLastMonth, endDate: endOfLastMonth };

  return { startDate: addDays(today, -6), endDate: today };
}

function getRangeParams(rangeKey, customRange) {
  if (rangeKey === 'custom-range') {
    const [startDate, endDate] = customRange ?? [];

    if (!startDate || !endDate) return null;

    return {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
    };
  }

  const range = getDateRangeForPreset(rangeKey);

  return {
    startDate: formatDateParam(range.startDate),
    endDate: formatDateParam(range.endDate),
  };
}

function normalizeComparableText(value) {
  return String(value ?? '').trim().toLowerCase();
}

function toTitleCase(value) {
  return String(value ?? '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getModuleSummaryName(moduleName) {
  const normalized = normalizeComparableText(moduleName);

  if (normalized.includes('candidate')) return 'Candidate';
  if (normalized.includes('submission')) return 'Submission';
  if (normalized.includes('job')) return 'Job';

  return toTitleCase(moduleName || 'List');
}

function getPayloadData(response) {
  return response?.data ?? response;
}

function buildSummaryPayload(summaryRequest, params) {
  const payload = {
    module: summaryRequest.module,
    dateRange: {
      startDate: params.startDate,
      endDate: params.endDate,
    },
  };

  if (summaryRequest.summary) {
    payload.summary = summaryRequest.summary;
  }
  if (Array.isArray(summaryRequest.items) && summaryRequest.items.length > 0) {
    payload.items = summaryRequest.items;
  }
  if (Array.isArray(summaryRequest.keys) && summaryRequest.keys.length > 0) {
    payload.keys = summaryRequest.keys;
  }
  if (summaryRequest.dateField) {
    payload.dateRange.key = summaryRequest.dateField;
  }

  return payload;
}

export default function ViewSummaryWidget({
  moduleName,
  open,
  onClose,
}) {
  const moduleSummaryName = getModuleSummaryName(moduleName);
  const normalizedModule = normalizeComparableText(moduleName);
  const isSubmission = normalizedModule.includes('submission');
  const isJob = normalizedModule.includes('job');
  const isCandidate = normalizedModule.includes('candidate');
  const summaryRequest = getSummaryRequest(moduleName);
  const defaultRange = isSubmission ? 'last-30-days' : 'last-7-days';
  const [selectedRange, setSelectedRange] = useState(defaultRange);
  const [customRange, setCustomRange] = useState(null);
  const [jobSummary, setJobSummary] = useState(null);
  const [candidateSummary, setCandidateSummary] = useState(null);
  const [submissionSummaryData, setSubmissionSummaryData] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const jobItems = getJobSummaryItemsFromApi(jobSummary);
  const candidateSummaryItems = getCandidateSummaryItems(candidateSummary);
  const submissionSummary = getSubmissionSummaryFromApi(submissionSummaryData);
  const totalSubmissions = Math.max(submissionSummary.total, 1);
  const internalPercent = Math.round((submissionSummary.internalCount / totalSubmissions) * 100);
  const clientPercent = Math.max(0, 100 - internalPercent);
  const showCustomRange = selectedRange === 'custom-range';

  useEffect(() => {
    if (!open) return;

    setSelectedRange(defaultRange);
    setCustomRange(null);
  }, [defaultRange, open]);

  useEffect(() => {
    if (!open || !summaryRequest) return undefined;

    const params = getRangeParams(selectedRange, customRange);

    if (!params) return undefined;

    let cancelled = false;
    setSummaryError('');

    const summaryRequestPromise = getDynamicCommonCount(
      buildSummaryPayload(summaryRequest, params),
    );

    summaryRequestPromise
      .then((response) => {
        if (cancelled) return;
        const data = getPayloadData(response);

        if (isJob) {
          setJobSummary(data);
          setCandidateSummary(null);
          setSubmissionSummaryData(null);
          return;
        }

        if (isCandidate) {
          setCandidateSummary(data);
          setJobSummary(null);
          setSubmissionSummaryData(null);
          return;
        }

        setSubmissionSummaryData(data);
        setJobSummary(null);
        setCandidateSummary(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setJobSummary(null);
        setCandidateSummary(null);
        setSubmissionSummaryData(null);
        setSummaryError(error.message || 'Could not load summary');
      });

    return () => {
      cancelled = true;
    };
  }, [customRange, isCandidate, isJob, isSubmission, open, selectedRange, summaryRequest]);

  if (!open) return null;

  return (
    <div className="view-summary-layer" role="presentation">
      <div className="view-summary-scrim" />
      <Card
        aria-label={`${moduleSummaryName} summary`}
        bordered={false}
        className={`view-summary-card view-summary-card--${isSubmission ? 'submission' : 'simple'}`}
      >
        <Button
          aria-label="Close summary"
          className="view-summary-close"
          icon={<CloseOutlined />}
          type="text"
          onClick={onClose}
        />
        <header className="view-summary-header">
          <AppTypography
            className="view-summary-title"
            color="primary"
            size="md"
            tag="h2"
            variant="h2"
            weight="medium"
          >
            {moduleSummaryName} Summary
          </AppTypography>
          <Select
            aria-label="Summary date range"
            className="view-summary-range"
            popupClassName="view-summary-range-dropdown"
            getPopupContainer={getSummaryPopupContainer}
            popupMatchSelectWidth={false}
            value={selectedRange}
            options={DATE_RANGE_OPTIONS}
            onChange={(value) => {
              setSelectedRange(value);
              if (value !== 'custom-range') setCustomRange(null);
            }}
          />
        </header>

        {isSubmission ? (
          <>
            {showCustomRange && (
              <RangePicker
                allowClear
                className="view-summary-custom-range view-summary-custom-range--full"
                format="MMM DD, YYYY"
                getPopupContainer={getSummaryPopupContainer}
                placeholder={['Start date', 'End date']}
                value={customRange}
                onChange={setCustomRange}
              />
            )}
            <section className="view-summary-submission-top" aria-label="Submission totals">
              <div
                className="view-summary-donut"
                style={{
                  '--summary-internal': `${internalPercent}%`,
                  '--summary-client': `${clientPercent}%`,
                }}
              >
                <AppTypography
                  color="success"
                  size="2xl"
                  tag="span"
                  variant="metric"
                  weight="semibold"
                >
                  {submissionSummary.total}
                </AppTypography>
                <AppTypography
                  color="success"
                  size="xs"
                  tag="span"
                  variant="caption"
                  weight="semibold"
                >
                  Total
                </AppTypography>
              </div>
              <div className="view-summary-legend">
                <AppTypography color="muted" size="sm" tag="span" variant="body" weight="regular">
                  <i className="view-summary-dot view-summary-dot--internal" />
                  Internal
                  <AppTypography color="primary" size="sm" tag="b" variant="body" weight="regular">
                    {submissionSummary.internalCount}
                  </AppTypography>
                </AppTypography>
                <AppTypography color="muted" size="sm" tag="span" variant="body" weight="regular">
                  <i className="view-summary-dot view-summary-dot--client" />
                  Client
                  <AppTypography color="primary" size="sm" tag="b" variant="body" weight="regular">
                    {submissionSummary.clientCount}
                  </AppTypography>
                </AppTypography>
              </div>
            </section>
            <div className="view-summary-divider" />
            <section className="view-summary-breakdown">
              <AppTypography className="view-summary-subtitle" color="primary" size="md" tag="h3" variant="h4" weight="medium">
                Internal Breakdown
              </AppTypography>
              {submissionSummary.internalBreakdown.length > 0 ? (
                submissionSummary.internalBreakdown.map((item) => (
                  <div className="view-summary-row view-summary-row--bullet" key={`internal-${item.label}`}>
                    <AppTypography color="muted" size="sm" tag="span" variant="body" weight="regular">
                      <i className="view-summary-dot view-summary-dot--internal" />
                      {item.label}
                    </AppTypography>
                    <AppTypography color="primary" size="sm" tag="b" variant="body" weight="regular">
                      {item.count}
                    </AppTypography>
                  </div>
                ))
              ) : (
                <AppTypography color="muted" size="sm" tag="p" variant="body" weight="regular">
                  No internal data found
                </AppTypography>
              )}
              <AppTypography className="view-summary-subtitle" color="primary" size="md" tag="h3" variant="h4" weight="medium">
                Client Breakdown
              </AppTypography>
              {submissionSummary.clientBreakdown.length > 0 ? (
                submissionSummary.clientBreakdown.map((item) => (
                  <div className="view-summary-row view-summary-row--bullet" key={`client-${item.label}`}>
                    <AppTypography color="muted" size="sm" tag="span" variant="body" weight="regular">
                      <i className="view-summary-dot view-summary-dot--client" />
                      {item.label}
                    </AppTypography>
                    <AppTypography color="primary" size="sm" tag="b" variant="body" weight="regular">
                      {item.count}
                    </AppTypography>
                  </div>
                ))
              ) : (
                <AppTypography color="muted" size="sm" tag="p" variant="body" weight="regular">
                  No client data found
                </AppTypography>
              )}
              {summaryError && (
                <AppTypography color="danger" size="xs" tag="p" variant="caption">
                  {summaryError}
                </AppTypography>
              )}
            </section>
          </>
        ) : (
          <section className="view-summary-breakdown">
            <div className="view-summary-section-head">
              <AppTypography
                className="view-summary-section-title"
                color="primary"
                size="md"
                tag="h3"
                variant="h3"
                weight="medium"
              >
                All {moduleSummaryName}
              </AppTypography>
              {showCustomRange && (
                <RangePicker
                  allowClear
                  className="view-summary-custom-range"
                  format="MMM DD, YYYY"
                  getPopupContainer={getSummaryPopupContainer}
                  placeholder={['Start date', 'End date']}
                  value={customRange}
                  onChange={setCustomRange}
                />
              )}
            </div>
            <div className="view-summary-divider" />
            {normalizedModule.includes('job') && (
              <AppTypography
                className="view-summary-breakdown-title"
                color="primary"
                size="sm"
                tag="h4"
                variant="h4"
                weight="regular"
              >
                Breakdown
              </AppTypography>
            )}
            {(isCandidate ? candidateSummaryItems : jobItems).map((item) => (
              <div className="view-summary-row" key={item.label}>
                <AppTypography color="muted" size="sm" tag="span" variant="body">
                  {item.label}
                </AppTypography>
                <AppTypography color="primary" size="sm" tag="b" variant="body">
                  {item.count}
                </AppTypography>
              </div>
            ))}
            {summaryError && (
              <AppTypography color="danger" size="xs" tag="p" variant="caption">
                {summaryError}
              </AppTypography>
            )}
          </section>
        )}
      </Card>
    </div>
  );
}
